import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./TimeInput.css";

/** Utility for startDate checks */
const getToday = (): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const isSameDay = (date1: Date | null, date2: Date | null): boolean => {
  return (
    date1 &&
    date2 &&
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  ) as boolean;
};

interface TimeInputProps {
  heading: string;
  value: string;
  onChange: (value: string) => void;
  height?: number;
  rtcButton?: boolean;
  handleRTC?: (value: string) => void;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  type?: "startTime" | "endTime";
  startTime?: string;
  startDate?: Date;
  endTime?: string;
  minTime?: string;
  rtcChecked?: boolean;
}

const TimeInput: React.FC<TimeInputProps> = ({
  heading,
  value,
  onChange,
  height,
  rtcButton,
  handleRTC,
  error,
  errorMessage,
  disabled = false,
  type,
  startTime,
  startDate,
  endTime,
  minTime,
  rtcChecked,
}) => {
  const [open, setOpen] = useState(false);
  const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});
  const clockList = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("");
  const [rtcCheck, setRtcCheck] = useState(false);

  const minutesStep = 15;

  /** Sync from external value */
  useEffect(() => {
    if (value) {
      const [hr, min] = value.split(":");
      setHour(hr || "");
      setMinute(min || "");
    } else {
      setHour("");
      setMinute("");
    }
  }, [value]);

  useEffect(() => {
    setRtcCheck(!!rtcChecked);
  }, [rtcChecked]);

  /** Close popup if clicked outside */
  useEffect(() => {
    const closeOpenMenus = (e: MouseEvent) => {
      if (clockList.current && open && !clockList.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOpenMenus);
    return () => document.removeEventListener("mousedown", closeOpenMenus);
  }, [open]);

  /** Popup styling */
  // useEffect(() => {
  //   if (open && inputRef.current) {
  //     setPopupStyle({
  //       position: "absolute",
  //       top: "100%",
  //       left: 0,
  //       width: "100%",
  //       zIndex: 999,
  //     });
  //   }
  // }, [open]);

  useEffect(() => {
    if (open && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setPopupStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    }
  }, [open]);


  /** StartTime restriction (now + 3h, rounded to next 15min) */
  let minStartHour: number | null = null;
  let minStartMinute: number | null = null;
  if (type === "startTime") {
    const now = new Date();
    now.setHours(now.getHours() + 3);
    minStartHour = now.getHours();
    minStartMinute = now.getMinutes();
    if (minStartMinute % minutesStep !== 0) {
      minStartMinute = Math.ceil(minStartMinute / minutesStep) * minutesStep;
      if (minStartMinute === 60) {
        minStartMinute = 0;
        minStartHour += 1;
      }
    }
  }

  /** EndTime restriction (relative to startTime) */
  /** EndTime restriction (relative to minTime passed from parent) */
  let startHour: number | null = null,
    startMinute: number | null = null;
  if (type === "endTime" && minTime) {
    const [sh, sm] = minTime.split(":");
    startHour = parseInt(sh, 10);
    startMinute = parseInt(sm, 10);
  }

  // Determine if this is an expiry time field
  const isExpiryTime =
    typeof heading === "string" &&
    (heading?.toLowerCase().includes("listing expiry time") ||
      heading?.toLowerCase().includes("terms expiry time"));

  /** Hours range, capped for startTime and endTime */
  let hours: string[];
  if (isExpiryTime) {
    hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, "0")); // 00 to 23 only
  } else if (type === "startTime") {
    hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, "0"));
  } else {
    hours = Array.from({ length: 25 }, (_, i) => i.toString().padStart(2, "0"));
  }
  /** Build Minutes */
  let minutes: string[] =
    hour === "24" && type === "endTime"
      ? ["00"]
      : Array.from({ length: 60 }, (_, i) => i)
          .filter((i) => i % minutesStep === 0)
          .map((i) => i.toString().padStart(2, "0"));

  /** Apply startTime filtering with startDate check */
  let minHour: number | null = null;
  let minMinute: number | null = null;

  if (minTime) {
    const [mh, mm] = minTime.split(":").map(Number);
    minHour = mh;
    minMinute = mm;
  }
  if (type === "startTime" && minHour !== null && minMinute !== null) {
    hours = hours.filter((hr) => parseInt(hr, 10) >= minHour!);

    if (hour === String(minHour).padStart(2, "0")) {
      minutes = minutes.filter((m) => parseInt(m, 10) >= minMinute!);

      if (minutes.length === 0) {
        hours = hours.filter((hr) => parseInt(hr, 10) > minHour!);
      }
    }
  }
  /** Apply endTime filtering */
  if (type === "endTime" && startHour !== null) {
    hours = hours.filter((hr) => parseInt(hr, 10) >= startHour!);
    if (hour === startHour.toString().padStart(2, "0")) {
      // strictly greater minutes
      minutes = minutes.filter((m) => parseInt(m, 10) > startMinute!);
      // if no minutes left in this hour, move to next hour
      if (minutes.length === 0) {
        hours = hours.filter((hr) => parseInt(hr, 10) > startHour!);
      }
    }
  }

  /** Handle selection */
  const handleHourInput = (hr: string) => {
    setHour(hr);
    if (minute) {
      onChange(`${hr}:${minute}`);
      setOpen(false);
    }
  };

  const handleMinuteInput = (min: string) => {
    setMinute(min);
    if (hour) {
      onChange(`${hour}:${min}`);
      setOpen(false);
    }
  };

  /** Handle RTC (24:00) */
  const handleRTCCheck = () => {
    if (rtcCheck) {
      setRtcCheck(false);
      if (handleRTC) handleRTC("");
    } else {
      setRtcCheck(true);
      if (handleRTC) handleRTC("24:00");
    }
  };

  return (
    <div ref={inputRef} className="time-picker-wrapper">
      {heading && (
        <div className={`display_flex_row_center contentTimeHeader`}>
          <div className="contentHeading">
            {heading}{" "}
            {!isExpiryTime && type === "startTime" && (
              <div style={{ position: "relative", display: "inline-block" }}>
                <div
                  style={{ position: "absolute", top: "-22px", left: "70px" }}
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  <button className="inline-flex items-center justify-center p-2">
                    <svg
                      className="w-3.5 h-4"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
                    </svg>
                  </button>
                  {showTooltip && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-900 rounded whitespace-nowrap z-50">
                      Start Time should be at least 3 hours from current time
                      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {rtcButton && (
            <div
              className={`display_flex_row_gap_4 anotherClass cursor-pointer`}
              onClick={handleRTCCheck}
            >
              <span
                className="contentHeading"
                style={{
                  color: "var(--secondary-color-text)",
                  fontWeight: "500",
                }}
              >
                RTC
              </span>
              {rtcCheck ? (
                <svg
                  className="w-3 h-3 mr-[40%]"
                  style={{ color: "var(--secondary-color-text)" }}
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
              ) : (
                <svg
                  className="w-3 h-3 mr-[40%]"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                </svg>
              )}
            </div>
          )}
        </div>
      )}

      {/* Input Box */}
      <div
        className={`input_select_cont ${error ? "inputErrorContainer" : ""}`}
        style={{
          height: `${height || "45"}px`,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.8 : 1,
          backgroundColor: disabled ? "#e9e9e9" : "white",
        }}
        onClick={() => {
          if (!disabled) setOpen((p) => !p);
        }}
      >
        <div className="flex items-center justify-center w-full h-full">
          <input
            type="text"
            maxLength={2}
            value={hour}
            disabled={disabled}
            placeholder="HH"
            className="w-7 text-center border-none outline-none bg-transparent text-[#8C8A96]"
            style={{
              fontSize: "14px",
              fontWeight: "500",
            }}
            onChange={(e) => {
              const val = e.target.value;
              if (/^\d{0,2}$/.test(val) && (val === "" || Number(val) <= 24)) {
                setHour(val);
                if (minute) onChange(`${val}:${minute}`);
              }
            }}
          />
          <span
            className="select-none mx-1"
            style={{ color: "#555", fontSize: "14px" }}
          >
            :
          </span>
          <input
            type="text"
            maxLength={2}
            value={minute}
            disabled={disabled}
            placeholder="MM"
            className="w-7 text-center border-none outline-none bg-transparent text-[#8C8A96]"
            style={{
              fontSize: "14px",
              fontWeight: "500",
            }}
            onChange={(e) => {
              const val = e.target.value;
              const validOptions = ["00", "15", "30", "45"];
              if (
                val === "" ||
                validOptions.some((opt) => opt.startsWith(val))
              ) {
                setMinute(val);
                if (hour) onChange(`${hour}:${val}`);
              }
            }}
            onBlur={() => {
              const validOptions = ["00", "15", "30", "45"];
              if (!minute) return;
              if (validOptions.includes(minute)) return;

              const numVal = Number(minute);
              let nearest = validOptions.reduce((a, b) =>
                Math.abs(Number(a) - numVal) < Math.abs(Number(b) - numVal)
                  ? a
                  : b,
              );
              setMinute(nearest);
              if (hour) onChange(`${hour}:${nearest}`);
            }}
          />
        </div>
      </div>

      {/* Error */}
      {error && errorMessage && (
        <div className="timeErrorMessage">{errorMessage}</div>
      )}

      {/* Popup */}
      {open && (
        <div className="pop-up-body" style={popupStyle}>
          <div style={{ display: "flex" }}>
            <div className="hr-list">
              {hours.map((hr) => (
                <div
                  key={hr}
                  onClick={() => handleHourInput(hr)}
                  className={`list-item ${hr === hour ? "selected" : ""}`}
                >
                  {hr}
                </div>
              ))}
            </div>
            <div className="hr-list">
              {minutes.map((min) => (
                <div
                  key={min}
                  onClick={() => handleMinuteInput(min)}
                  className={`list-item ${min === minute ? "selected" : ""}`}
                >
                  {min}
                </div>
              ))}
            </div>
          </div>
          <div className="clock-footer">
            <button
              className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
              onClick={() => setOpen(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeInput;